import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Form } from 'react-bootstrap';
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import { useLanguage } from '../../services/language/LanguageContext';
import { DateTimeFormat } from '../../helper/Formatters';
import ImageViewer from '../../components/image-viewer/ImageViewer';
import ImageUpload from '../../components/image-upload';
import { toast } from 'react-toastify';

const InfoRow = ({ label, value }) => (
    <div style={{ display: 'flex', gap: 8, marginBottom: 10, fontSize: 13 }}>
        <span style={{ color: '#9ca3af', minWidth: 110, flexShrink: 0 }}>{label}</span>
        <span style={{ color: '#1f2937', fontWeight: 500 }}>{value || '-'}</span>
    </div>
);

const Settings = () => {
    const api = useApiServices();
    const { t } = useLanguage();
    const [data, setData] = useState(null);
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', email: '', phone: '',
    });
    const [pwForm, setPwForm] = useState({ newPassword: '', confirmPassword: '' });
    const [changingPw, setChangingPw] = useState(false);
    const [showNewPw, setShowNewPw] = useState(false);
    const [showConfirmPw, setShowConfirmPw] = useState(false);

    const fetchInfo = async () => {
        const { isSuccess, result } = await api.AuthInfoAsync();
        if (isSuccess) {
            setData(result);
            setFormData({
                firstName: result.firstName ?? '',
                lastName: result.lastName ?? '',
                email: result.email ?? '',
                phone: result.phone ?? '',
            });
        }
    };

    useEffect(() => { fetchInfo(); }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePwChange = (e) => {
        const { name, value } = e.target;
        setPwForm(prev => ({ ...prev, [name]: value }));
    };

    const handlePwSubmit = async (e) => {
        e.preventDefault();
        if (pwForm.newPassword.length < 6) {
            toast.error(t('password_min_length'));
            return;
        }
        if (pwForm.newPassword !== pwForm.confirmPassword) {
            toast.error(t('password_mismatch'));
            return;
        }
        setChangingPw(true);
        const { isSuccess } = await api.ChangePasswordAsync({ password: pwForm.newPassword });
        setChangingPw(false);
        if (isSuccess) {
            toast.success(t('change_password_success'));
            setPwForm({ newPassword: '', confirmPassword: '' });
        } else {
            toast.error(t('change_password_error'));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!data) return;
        setSubmitting(true);
        const { isSuccess } = await api.UpdateUserByIdAsync(data.id, {
            ...formData,
            image: file?.id ?? (data.image ?? null),
        });
        setSubmitting(false);
        if (isSuccess) {
            toast.success('บันทึกข้อมูลแล้ว');
            fetchInfo();
            setFile(null);
        } else {
            toast.error('บางอย่างผิดพลาด');
        }
    };

    const fullName = data ? `${data.firstName || ''} ${data.lastName || ''}`.trim() || '-' : '-';
    const initials = data
        ? `${(data.firstName || '')[0] || ''}${(data.lastName || '')[0] || ''}`.toUpperCase() || '?'
        : '?';

    return (
        <Row className="g-3">
            {/* ── Left: Profile Card ── */}
            <Col md={4}>
                <Card style={cardStyle}>
                    <Card.Body style={{ textAlign: 'center', padding: '32px 24px 24px' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                            {data?.image ? (
                                <div style={{ borderRadius: '50%', overflow: 'hidden', border: '3px solid #e0e7ff' }}>
                                    <ImageViewer id={data.image} width={80} preview={false} />
                                </div>
                            ) : (
                                <div style={{
                                    width: 80, height: 80, borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 28, fontWeight: 700, color: '#fff',
                                    border: '3px solid #e0e7ff',
                                }}>
                                    {initials}
                                </div>
                            )}
                        </div>

                        <div style={{ fontSize: 18, fontWeight: 700, color: '#1e3a5f', marginBottom: 4 }}>{fullName}</div>

                        <span style={{
                            display: 'inline-block', fontSize: 11, fontWeight: 600,
                            background: data?.userType?.id === 0 ? '#fef3c7' : '#e0e7ff',
                            color: data?.userType?.id === 0 ? '#92400e' : '#4f46e5',
                            border: `1px solid ${data?.userType?.id === 0 ? '#fcd34d' : '#c7d2fe'}`,
                            borderRadius: 20, padding: '3px 12px', marginBottom: 20,
                        }}>
                            {data?.userType?.name || '-'}
                        </span>

                        <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 16, textAlign: 'left' }}>
                            <InfoRow label={t('username')} value={data?.username} />
                            <InfoRow label={t('status')} value={data?.isActived ? t('active') : t('inactive')} />
                            <InfoRow label={t('member_since')} value={DateTimeFormat(data?.createDate)} />
                        </div>
                    </Card.Body>
                </Card>
            </Col>

            {/* ── Right: Edit Form ── */}
            <Col md={8}>
                <Card style={cardStyle}>
                    <Card.Body style={{ padding: '24px 28px' }}>
                        <div style={{ marginBottom: 20 }}>
                            <div style={sectionTitle}>{t('edit_profile')}</div>
                            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{t('profile_subtitle')}</div>
                        </div>

                        <Form onSubmit={handleSubmit}>
                            <Form.Group className="mb-4">
                                <Form.Label style={labelStyle}>{t('profile_photo')}</Form.Label>
                                <ImageUpload forceCrop={true} onUploadComplete={(f) => setFile(f)} />
                            </Form.Group>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={labelStyle}>{t('first_name')}</Form.Label>
                                        <Form.Control name="firstName" value={formData.firstName} onChange={handleChange} style={{ fontSize: 14, borderRadius: 8 }} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={labelStyle}>{t('last_name')}</Form.Label>
                                        <Form.Control name="lastName" value={formData.lastName} onChange={handleChange} style={{ fontSize: 14, borderRadius: 8 }} />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={labelStyle}>{t('email')}</Form.Label>
                                        <Form.Control name="email" type="email" value={formData.email} onChange={handleChange} style={{ fontSize: 14, borderRadius: 8 }} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={labelStyle}>{t('phone')}</Form.Label>
                                        <Form.Control name="phone" value={formData.phone} onChange={handleChange} style={{ fontSize: 14, borderRadius: 8 }} />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-4">
                                <Form.Label style={labelStyle}>{t('username')}</Form.Label>
                                <Form.Control value={data?.username ?? ''} readOnly style={{ fontSize: 14, borderRadius: 8, background: '#f9fafb', color: '#9ca3af', fontFamily: 'monospace', cursor: 'not-allowed' }} />
                                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{t('username_readonly')}</div>
                            </Form.Group>

                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button type="submit" disabled={submitting || !data} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                    padding: '9px 22px',
                                    background: submitting ? '#93c5fd' : 'linear-gradient(135deg, #1e3a5f, #2d6a9f)',
                                    border: 'none', borderRadius: 10,
                                    color: '#fff', fontSize: 14, fontWeight: 600,
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 2px 8px rgba(30,58,95,0.25)',
                                }}>
                                    {submitting
                                        ? <><i className="fa fa-spinner fa-spin" /> {t('saving')}</>
                                        : <><i className="fa fa-save" /> {t('save')}</>
                                    }
                                </button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>

                <Card style={{ ...cardStyle, marginTop: 16 }}>
                    <Card.Body style={{ padding: '20px 28px' }}>
                        <div style={{ ...sectionTitle, marginBottom: 14 }}>{t('account_info')}</div>
                        <Row>
                            <Col md={6}>
                                <InfoRow label={t('user_id')} value={
                                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#6b7280', wordBreak: 'break-all' }}>{data?.id}</span>
                                } />
                                <InfoRow label={t('user_type')} value={data?.userType?.name} />
                            </Col>
                            <Col md={6}>
                                <InfoRow label={t('entity_id')} value={
                                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#6b7280', wordBreak: 'break-all' }}>{data?.entityUniqueIdentifierId}</span>
                                } />
                                <InfoRow label={t('status')} value={
                                    <span style={{
                                        fontSize: 11, fontWeight: 600, borderRadius: 8, padding: '2px 8px',
                                        background: data?.isActived ? '#f0fdf4' : '#fef2f2',
                                        color: data?.isActived ? '#16a34a' : '#dc2626',
                                        border: `1px solid ${data?.isActived ? '#bbf7d0' : '#fecaca'}`,
                                    }}>
                                        {data?.isActived ? t('active') : t('inactive')}
                                    </span>
                                } />
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* ── Change Password ── */}
                <Card style={{ ...cardStyle, marginTop: 16 }}>
                    <Card.Body style={{ padding: '24px 28px' }}>
                        <div style={{ marginBottom: 20 }}>
                            <div style={sectionTitle}>{t('change_password')}</div>
                            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{t('change_password_subtitle')}</div>
                        </div>
                        <Form onSubmit={handlePwSubmit}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={labelStyle}>{t('new_password')}</Form.Label>
                                        <div style={{ position: 'relative' }}>
                                            <Form.Control
                                                name="newPassword"
                                                type={showNewPw ? 'text' : 'password'}
                                                value={pwForm.newPassword}
                                                onChange={handlePwChange}
                                                style={{ fontSize: 14, borderRadius: 8, paddingRight: 38 }}
                                                autoComplete="new-password"
                                            />
                                            <button type="button" onClick={() => setShowNewPw(v => !v)}
                                                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}>
                                                <i className={`fa ${showNewPw ? 'fa-eye-slash' : 'fa-eye'}`} />
                                            </button>
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={labelStyle}>{t('confirm_password')}</Form.Label>
                                        <div style={{ position: 'relative' }}>
                                            <Form.Control
                                                name="confirmPassword"
                                                type={showConfirmPw ? 'text' : 'password'}
                                                value={pwForm.confirmPassword}
                                                onChange={handlePwChange}
                                                style={{ fontSize: 14, borderRadius: 8, paddingRight: 38 }}
                                                autoComplete="new-password"
                                            />
                                            <button type="button" onClick={() => setShowConfirmPw(v => !v)}
                                                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}>
                                                <i className={`fa ${showConfirmPw ? 'fa-eye-slash' : 'fa-eye'}`} />
                                            </button>
                                        </div>
                                        {pwForm.confirmPassword && pwForm.newPassword !== pwForm.confirmPassword && (
                                            <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>{t('password_mismatch')}</div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button type="submit" disabled={changingPw || !pwForm.newPassword || !pwForm.confirmPassword} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                    padding: '9px 22px',
                                    background: changingPw ? '#93c5fd' : 'linear-gradient(135deg, #1e3a5f, #2d6a9f)',
                                    border: 'none', borderRadius: 10,
                                    color: '#fff', fontSize: 14, fontWeight: 600,
                                    cursor: (changingPw || !pwForm.newPassword || !pwForm.confirmPassword) ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 2px 8px rgba(30,58,95,0.25)',
                                    opacity: (!pwForm.newPassword || !pwForm.confirmPassword) ? 0.6 : 1,
                                }}>
                                    {changingPw
                                        ? <><i className="fa fa-spinner fa-spin" /> {t('changing_password')}</>
                                        : <><i className="fa fa-lock" /> {t('change_password')}</>
                                    }
                                </button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

const cardStyle = { borderRadius: 12, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' };
const sectionTitle = { fontSize: 14, fontWeight: 700, color: '#1e3a5f' };
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };

export default Settings;
